import { prepareReimburseTree } from "@/utils/reimburseCategory";
import { ReimburseTreeSelect } from "@/components/Selector";
import DetailModal from "@/components/DetailModal";
import { ApplicationTypeEnum, ReimbursRequest, RequestReimbursementItem, RequestDailyItem, ReimburseCategory, ReimbursRequestStep, ApproveStepStatusEnum, AssignTypeEnum, ApproveStatusEnum, ApproveTypeEnum } from "@/types/reimburse";
import { FC, useEffect, useState, useMemo, useContext } from "react";
import { requestFetch, treeList, rmReimburseItem, rmRDailyItem, ApproveType, dailypdate, reimburseUpdate } from "@/apis/reimburse";
import { Spin, Descriptions, Timeline, Tag, Typography, Space, Flex, Button, App, Input, notification, Tooltip, Dropdown } from "antd";
import { ColumnsType } from "antd/es/table";
import { CheckCircleFilled, ClockCircleFilled, CloseCircleFilled, RollbackOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import ApproveHeader from "./ApproveHeader";
import AddOrEditReimburseItem from "./AddOrEditReimburseItem";
import AddOrEditDailyItem from "./AddOrEditDailyItem";
import { RequestContentWrap } from "../ApprovalDetail/style";
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";
import TabDocTable, { OpenTabObj } from "@/components/TabDocTable";
import { AnyObject } from "antd/es/_util/type";
import iconFolder from "@/assets/images/fileIcon/folder.svg";
import { DictContext } from "@/layout/context/DictContext";
import { numFormmater } from "@/pages/Requirement/util";

interface DetailAndApproveProps {
  detail: ReimbursRequest;
  open: boolean;
  onClose: () => void;
  onSuccess: (data: ReimbursRequest) => void;
};

const DetailAndApprove: FC<DetailAndApproveProps> = ({ detail, open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { pageId } = usePage();
  const { currencys } = useContext(DictContext);


  const { Text } = Typography;
  const { modal } = App.useApp();

  const [treeLoading, setTreeLoading] = useState(false);
  const [treeData, setTreeData] = useState<ReimburseCategory[]>([]);
  const preparedTree = useMemo(() => prepareReimburseTree(treeData), [treeData]);

  const [reimbursRequest, setReimbursRequest] = useState<ReimbursRequest>({ ...detail });
  const [loading, setLoading] = useState<boolean>(false);

  // 报销项目弹窗状态
  const [openReimburseItemModal, setOpenReimburseItemModal] = useState(false);
  const [editingReimburseItem, setEditingReimburseItem] = useState<RequestReimbursementItem | null>(null);

  // 日用品项目弹窗状态
  const [openDailyItemModal, setOpenDailyItemModal] = useState(false);
  const [editingDailyItem, setEditingDailyItem] = useState<RequestDailyItem | null>(null);
  const [productMode, setProductMode] = useState<"CODE" | "NO_CODE">("NO_CODE");

  const [hasUpdate, setHasUpdate] = useState<boolean>(false);

  // 附件标签页状态
  const [openTab, setOpenTab] = useState<{ itemId: string } & OpenTabObj | undefined>(undefined);

  // 排序后的步骤数据
  const sortedSteps = useMemo(() => {
    if (!reimbursRequest.steps || reimbursRequest.steps.length === 0) return [];
    return [...reimbursRequest.steps].sort((a, b) => a.stepNo - b.stepNo);
  }, [reimbursRequest.steps]);

  const { canEdit } = useMemo(() => {
    let canEdit = false;
    if (pageId === PageId.REIMBURSE_MINE && [ApproveStatusEnum.DRAFT, ApproveStatusEnum.RETURNED].includes(reimbursRequest.status)) canEdit = true
    return {
      canEdit
    }
  }, [reimbursRequest, pageId])

  // 获取步骤状态信息
  const getStepStatusInfo = (step: ReimbursRequestStep) => {
    switch (step.stepStatus) {
      case ApproveStepStatusEnum.PENDING:
        return {
          icon: <ClockCircleFilled style={{ color: "#1890ff" }} />,
          text: t("reimburse.statusPending"),
          color: "processing",
        };
      case ApproveStepStatusEnum.APPROVED:
        return {
          icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
          text: t("reimburse.statusApproved"),
          color: "success",
        };
      case ApproveStepStatusEnum.RETURNED:
        return {
          icon: <RollbackOutlined style={{ color: "#faad14" }} />,
          text: t("reimburse.statusReturned"),
          color: "warning",
        };
      case ApproveStepStatusEnum.REJECTED:
        return {
          icon: <CloseCircleFilled style={{ color: "#ff4d4f" }} />,
          text: t("reimburse.statusRejected"),
          color: "error",
        };
      default:
        return {
          icon: <ClockCircleFilled style={{ color: "#d9d9d9" }} />,
          text: "-",
          color: "default",
        };
    }
  };

  // 获取分配类型文本
  const getAssignTypeText = (assignType: AssignTypeEnum) => {
    switch (assignType) {
      case AssignTypeEnum.SPECIFIC_ORG_ANYONE:
        return t("reimburse.specificOrgAnyone");
      case AssignTypeEnum.APPLICANT_ORG_ANYONE:
        return t("reimburse.applicantOrgAnyone");
      case AssignTypeEnum.SPECIFIC_USERS:
        return t("reimburse.specificUsers");
      default:
        return "-";
    }
  };

  const getApproveTypeText = (approveType: ApproveTypeEnum) => {
    switch (approveType) {
      case ApproveTypeEnum.IN_ORDER:
        return t("reimburse.inOrder");
      case ApproveTypeEnum.ANY:
        return t("reimburse.any");
      case ApproveTypeEnum.LAST:
        return t("reimburse.last");
      default:
        return "-";
    }
  };
  useEffect(() => {
    if (detail.id) {
      if (detail.type === ApplicationTypeEnum.REIMBURSEMENT) fetchTreeData();
      setLoading(true);
      requestFetch(detail.id)
        .then(res => {
          setReimbursRequest({ ...res })
        })
        .catch(() => { })
        .finally(() => setLoading(false))
    }
  }, [detail])

  const fetchTreeData = () => {
    setTreeLoading(true);
    treeList()
      .then((res) => {
        setTreeData(res);
      })
      .catch(() => {
        setTreeData([]);
      })
      .finally(() => {
        setTreeLoading(false);
      });
  };

  const handleConfirm = (data: ReimbursRequest, confirmType: ApproveType | undefined) => {
    onSuccess(data);
    onClose();
  }

  // 打开新增报销项目弹窗
  const handleAddReimburseItem = () => {
    setEditingReimburseItem(null);
    setOpenReimburseItemModal(true);
  };

  // 打开编辑报销项目弹窗
  const handleEditReimburseItem = (item: RequestReimbursementItem) => {
    setEditingReimburseItem(item);
    setOpenReimburseItemModal(true);
  };

  // 删除报销项目
  const handleDeleteReimburseItem = (item: RequestReimbursementItem) => {
    modal.confirm({
      title: t("common.rmTip"),
      content: t("common.rmContent"),
      onOk: () => {
        rmReimburseItem(item.id)
          .then(() => {
            notification.success({ placement: 'top', message: t("common.rmSuccess") });
            if (reimbursRequest.reimbursement) {
              const newItems = reimbursRequest.reimbursement.items.filter(i => i.id !== item.id);
              const newTotalAmount = Number(newItems.reduce((sum, i) => sum + (i.amount || 0), 0).toFixed(2));

              setReimbursRequest({
                ...reimbursRequest,
                reimbursement: {
                  ...reimbursRequest.reimbursement,
                  items: newItems,
                  totalAmount: newTotalAmount,
                },
              });
              setHasUpdate(true);
            }
          })
          .catch(() => {
            notification.error({ placement: 'top', message: t("common.rmError") });
          });
      },
    });
  };

  // 报销项目操作成功回调 - 计算总金额并更新
  const handleReimburseItemSuccess = async (item: RequestReimbursementItem) => {
    if (reimbursRequest.reimbursement) {
      const existingIndex = reimbursRequest.reimbursement.items.findIndex(i => i.id === item.id);
      let newItems;
      if (existingIndex >= 0) {
        // 更新
        newItems = [...reimbursRequest.reimbursement.items];
        newItems[existingIndex] = item;
      } else {
        // 新增
        newItems = [...reimbursRequest.reimbursement.items, item];
      }

      // 计算总金额（保留两位小数）
      const newTotalAmount = Number(newItems.reduce((sum, i) => sum + (i.amount || 0), 0).toFixed(2));

      try {
        await reimburseUpdate(reimbursRequest.id, {
          title: reimbursRequest.title,
          content: reimbursRequest.content,
          totalAmount: newTotalAmount,
          remark: reimbursRequest?.reimbursement?.remark ?? '',
          items: []
        });
        setReimbursRequest({
          ...reimbursRequest,
          reimbursement: {
            ...reimbursRequest.reimbursement,
            items: newItems,
            totalAmount: newTotalAmount,
          },
        });
      } catch (error) {
        notification.error({ placement: 'top', message: t("common.saveFAil") });
      }
    }
  };

  // 打开新增日用品项目弹窗
  const handleAddDailyItem = (mode: "CODE" | "NO_CODE") => {
    setProductMode(mode);
    setEditingDailyItem(null);
    setOpenDailyItemModal(true);
  };

  // 打开编辑日用品项目弹窗
  const handleEditDailyItem = (item: RequestDailyItem) => {
    setEditingDailyItem(item);
    setOpenDailyItemModal(true);
  };

  // 删除日用品项目
  const handleDeleteDailyItem = (item: RequestDailyItem) => {
    modal.confirm({
      title: t("common.rmTip"),
      content: t("common.rmContent"),
      onOk: () => {
        rmRDailyItem(item.id)
          .then(() => {
            notification.success({ placement: 'top', message: t("common.rmSuccess") });
            // 直接从本地删除
            if (reimbursRequest.dailySupplies) {
              setReimbursRequest({
                ...reimbursRequest,
                dailySupplies: {
                  ...reimbursRequest.dailySupplies,
                  items: reimbursRequest.dailySupplies.items.filter(i => i.id !== item.id),
                },
              });
            }
          })
          .catch(() => {
            notification.error({ placement: 'top', message: t("common.rmError") });
          });
      },
    });
  };

  // 日用品项目操作成功回调 - 直接更新本地数据
  const handleDailyItemSuccess = (item: RequestDailyItem) => {
    if (reimbursRequest.dailySupplies) {
      const existingIndex = reimbursRequest.dailySupplies.items.findIndex(i => i.id === item.id);
      let newItems;
      if (existingIndex >= 0) {
        // 更新
        newItems = [...reimbursRequest.dailySupplies.items];
        newItems[existingIndex] = item;
      } else {
        // 新增
        newItems = [...reimbursRequest.dailySupplies.items, item];
      }
      setReimbursRequest({
        ...reimbursRequest,
        dailySupplies: {
          ...reimbursRequest.dailySupplies,
          items: newItems,
        },
      });
    }
  };

  const handleSave = () => {
    if (reimbursRequest.type === ApplicationTypeEnum.DAILY_SUPPLIES) {
      return dailypdate(reimbursRequest?.dailySupplies?.id ?? '', {
        title: reimbursRequest.title,
        content: reimbursRequest.content,
        purpose: reimbursRequest?.dailySupplies?.purpose ?? '',
        remark: reimbursRequest?.dailySupplies?.remark ?? '',
        items: []
      })
    } else {
      return reimburseUpdate(reimbursRequest.id, {
        title: reimbursRequest.title,
        content: reimbursRequest.content,
        totalAmount: reimbursRequest?.reimbursement?.totalAmount ?? 0,
        remark: reimbursRequest?.reimbursement?.remark ?? '',
        items: []
      })
    }
  }

  // 报销申请表格列
  const reimburseColumns: ColumnsType<RequestReimbursementItem> = [
    {
      title: t("reimburse.reimbursement"),
      dataIndex: "categoryId",
      width: 150,
      render: (val) => <ReimburseTreeSelect disabled value={val} treeData={preparedTree} />
    },
    {
      title: t("reimburse.amount"),
      dataIndex: "amount",
      width: 120,
      render: (val) => numFormmater(Number(val?.toFixed(2) || "0")),
    },
    {
      title: t("reimburse.invoiceCount"),
      dataIndex: "invoiceCount",
      width: 120,
    },
    {
      title: t("reimburse.expenseDate"),
      dataIndex: "expenseDate",
      width: 150,
      render: (val) => val ? dayjs(val).format("YYYY-MM-DD") : "-",
    },
    {
      title: t("common.desc"),
      dataIndex: "description",
      ellipsis: true,
    },
    {
      title: t("reimburse.remark"),
      dataIndex: "remark",
      ellipsis: true,
    },
    {
      title: t('common.attachment'),
      dataIndex: "docId",
      width: 60,
      fixed: "right",
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => setOpenTab({ itemId: item.id, docId: item.docId || "", name: `${t('common.attachment')}` })}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      width: 120,
      fixed: "right",
      hidden: !canEdit,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditReimburseItem(record)}
          >
            {t("common.edit")}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteReimburseItem(record)}
          >
            {t("common.del")}
          </Button>
        </Space>
      ),
    },
  ];

  // 日常用品申请表格列
  const dailyColumns: ColumnsType<RequestDailyItem> = [
    {
      title: t("product.code"),
      dataIndex: "productCode",
      width: 120,
    },
    {
      title: t("product.name"),
      dataIndex: "productName",
      width: 150,
    },
    {
      title: t("product.brand1"),
      dataIndex: "productModel",
      width: 320,
    },
    {
      title: t("reimburse.count"),
      dataIndex: "count",
      width: 140,
      render: (value) => numFormmater(value || 0),
    },
    {
      title: t("common.desc"),
      dataIndex: "desc",
      ellipsis: true,
    },
    {
      title: t('common.attachment'),
      dataIndex: "docId",
      width: 60,
      fixed: "right",
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => setOpenTab({ itemId: item.id, docId: item.docId || "", name: `${item.productName ? item.productName + '-' : ''} ${t('common.attachment')}` })}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      width: 120,
      fixed: "right",
      hidden: !canEdit,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditDailyItem(record)}
          >
            {t("common.edit")}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteDailyItem(record)}
          >
            {t("common.del")}
          </Button>
        </Space>
      ),
    },
  ];

  return <DetailModal
    logBookId={detail.logbookId}
    modalProps={{
      width: "100vw",
      height: "100vh",
      styles: {
        content: { height: "100vh" },
        body: { height: "calc(100vh - 60px)" },
      },
      title: <ApproveHeader detail={reimbursRequest} onConfirm={handleConfirm} pageId={pageId} onSave={handleSave} hasUpdate={hasUpdate} setHasUpdate={setHasUpdate} />,
      open,
      onCancel: onClose,
      footer: null
    }}>
    <RequestContentWrap>
      {/* 左侧审批步骤 */}
      {sortedSteps.length > 0 && (
        <div className="timeline-left-panel">
          {reimbursRequest.approveType && <div className="approvetype">
            <Typography.Title level={5} style={{ marginTop: -12, marginBottom: 12 }}>{getApproveTypeText(reimbursRequest.approveType)}</Typography.Title>
          </div>}
          <Timeline
            items={sortedSteps.map((step, index) => {
              const statusInfo = getStepStatusInfo(step);
              return {
                dot: statusInfo.icon,
                color: step.stepStatus === ApproveStepStatusEnum.APPROVED ? "#52c41a" :
                  step.stepStatus === ApproveStepStatusEnum.RETURNED ? "#faad14" :
                    step.stepStatus === ApproveStepStatusEnum.REJECTED ? "#ff4d4f" :
                      step.stepStatus === ApproveStepStatusEnum.PENDING ? "#1890ff" : "#d9d9d9",
                children: (
                  <div className="timeline-item-content">
                    <div className="timeline-item-top">
                      <Text strong>{index + 1}.</Text>
                      <Tag color={statusInfo.color} style={{ fontSize: 12, padding: "0 4px" }}>
                        {statusInfo.text}
                      </Tag>
                    </div>
                    <div className="timeline-item-middle">
                      <Text>审批类型：{getAssignTypeText(step.assignType)}</Text>
                      {step.assignType === AssignTypeEnum.SPECIFIC_USERS && step.candidates && step.candidates.length > 0 ? (
                        <div className="user-warp">
                          <span>{t("reimburse.approver")}: </span>
                          {step.candidates.map((u) => (
                            <div className="user-name">
                              <WMSAvatar
                                key={u.id}
                                uniChar={u.id}
                                avatarId={u.avatar}
                                creator={u}
                                alt={getUserAvatarTip(u)}
                                size={20}
                              />
                              <span>{getUserName(u)}</span>
                            </div>
                          ))}
                        </div>
                      ) : step.org ? (
                        <div>
                          <span>{t("reimburse.approveOrg")}：</span>
                          <Tag color="magenta">{step.org.name}</Tag>
                        </div>
                      ) : (
                        <>{t("reimburse.approver")}: -</>
                      )}
                    </div>
                    {step.approvalComment && (
                      <div className="timeline-item-bottom">
                        <Tooltip title={step.approvalComment} placement="rightTop">
                          <span>{t("reimburse.approveRemark")}: {step.approvalComment}</span>
                        </Tooltip>
                      </div>
                    )}
                    {step.stepStatus !== ApproveStepStatusEnum.PENDING && (
                      <div className="timeline-item-bottom">
                        <span>{t("reimburse.approveTime")}：</span>{dayjs(step.updatedAt).format("YYYY-MM-DD HH:mm:ss")}
                      </div>
                    )}
                  </div>
                ),
              };
            })}
          />
        </div>
      )}
      {/* 右侧内容区 */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ overflow: 'auto', paddingRight: 16 }}>
          {(loading || treeLoading) ? (
            <Spin style={{ marginTop: 16, width: "100%", height: 240 }} />
          ) : (
            <>
              <Descriptions
                column={3}
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Descriptions.Item span={3} label={<div className="desc-label">{t("reimburse.title")}</div>}>
                  {canEdit ? (
                    <Input
                      status={!!!reimbursRequest.title ? "error" : undefined}
                      value={reimbursRequest.title}
                      onChange={(e) => {
                        setReimbursRequest({ ...reimbursRequest, title: e.target.value });
                        setHasUpdate(true);
                      }}
                      placeholder={t("reimburse.plTitle")}
                      maxLength={200}
                    />
                  ) : (
                    reimbursRequest.title
                  )}
                </Descriptions.Item>
                <Descriptions.Item span={3} label={<div className="desc-label">{t("reimburse.content")}</div>} style={{ paddingBottom: canEdit ? 24 : undefined }}>
                  {canEdit ? (
                    <Input.TextArea
                      status={!!!reimbursRequest.content ? "error" : undefined}
                      value={reimbursRequest.content}
                      onChange={(e) => {
                        setReimbursRequest({ ...reimbursRequest, content: e.target.value });
                        setHasUpdate(true);
                      }}
                      placeholder={t("reimburse.plContent")}
                      autoSize={{ minRows: 2, maxRows: 2 }}
                      maxLength={500}
                      showCount
                    />
                  ) : (
                    reimbursRequest.content || "-"
                  )}
                </Descriptions.Item>
                {reimbursRequest.type === ApplicationTypeEnum.REIMBURSEMENT && reimbursRequest.reimbursement && (
                  <>
                    <Descriptions.Item span={3} label={<div className="desc-label">{t("reimburse.remark")}</div>} style={{ paddingBottom: canEdit ? 24 : undefined }}>
                      {canEdit ? (
                        <Input.TextArea
                          value={reimbursRequest.reimbursement?.remark || ""}
                          onChange={(e) => {
                            setReimbursRequest({
                              ...reimbursRequest,
                              reimbursement: {
                                ...reimbursRequest.reimbursement!,
                                remark: e.target.value,
                              },
                            });
                            setHasUpdate(true);
                          }}
                          placeholder={t("reimburse.plRemark")}
                          autoSize={{ minRows: 2, maxRows: 2 }}
                          maxLength={500}
                          showCount
                        />
                      ) : (
                        reimbursRequest.reimbursement?.remark || "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item span={1} label={<div className="desc-label">{t("reimburse.amount")}</div>}>
                      {reimbursRequest.reimbursement?.totalAmount?.toFixed(2) || "-"} {currencys.find(cur => cur.key === reimbursRequest.reimbursement?.currency + '')?.desc}
                    </Descriptions.Item>
                  </>
                )}
                {reimbursRequest.creator && reimbursRequest.type === ApplicationTypeEnum.REIMBURSEMENT && <Descriptions.Item label={<div className="desc-label">{t("reimburse.applicant")}</div>}>
                  <Flex align="center" gap={4}>
                    <WMSAvatar
                      uniChar={reimbursRequest.creator?.id ?? "1"}
                      avatarId={reimbursRequest.creator?.avatar ?? ""}
                      creator={reimbursRequest.creator}
                      alt={getUserAvatarTip(reimbursRequest.creator)}
                      size={24}
                    />
                    <span>{getUserName(reimbursRequest.creator)}</span>
                  </Flex>
                </Descriptions.Item>}
                {reimbursRequest.type === ApplicationTypeEnum.DAILY_SUPPLIES && reimbursRequest.dailySupplies && (
                  <>
                    <Descriptions.Item span={3} label={<div className="desc-label">{t("reimburse.purpose")}</div>}>
                      {canEdit ? (
                        <Input
                          value={reimbursRequest.dailySupplies?.purpose || ""}
                          onChange={(e) => {
                            setReimbursRequest({
                              ...reimbursRequest,
                              dailySupplies: {
                                ...reimbursRequest.dailySupplies!,
                                purpose: e.target.value,
                              },
                            });
                            setHasUpdate(true);
                          }}
                          placeholder={t("reimburse.plPurpose")}
                          maxLength={200}
                        />
                      ) : (
                        reimbursRequest.dailySupplies?.purpose || "-"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item span={3} label={<div className="desc-label">{t("reimburse.remark")}</div>} style={{ paddingBottom: canEdit ? 24 : undefined }}>
                      {canEdit ? (
                        <Input.TextArea
                          value={reimbursRequest.dailySupplies?.remark || ""}
                          onChange={(e) => {
                            setReimbursRequest({
                              ...reimbursRequest,
                              dailySupplies: {
                                ...reimbursRequest.dailySupplies!,
                                remark: e.target.value,
                              },
                            });
                            setHasUpdate(true);
                          }}
                          placeholder={t("reimburse.plRemark")}
                          autoSize={{ minRows: 2, maxRows: 2 }}
                          maxLength={500}
                          showCount
                        />
                      ) : (
                        reimbursRequest.dailySupplies?.remark || "-"
                      )}
                    </Descriptions.Item>
                  </>
                )}
                {reimbursRequest.creator && reimbursRequest.type === ApplicationTypeEnum.DAILY_SUPPLIES && <Descriptions.Item span={2} label={<div className="desc-label">{t("reimburse.applicant")}</div>}>
                  <Flex align="center" gap={4}>
                    <WMSAvatar
                      uniChar={reimbursRequest.creator?.id ?? "1"}
                      avatarId={reimbursRequest.creator?.avatar ?? ""}
                      creator={reimbursRequest.creator}
                      alt={getUserAvatarTip(reimbursRequest.creator)}
                      size={24}
                    />
                    <span>{getUserName(reimbursRequest.creator)}</span>
                  </Flex>
                </Descriptions.Item>}
                <Descriptions.Item label={<div className="desc-label">{t("common.createTime")}</div>}>
                  {dayjs(reimbursRequest.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                </Descriptions.Item>
                <Descriptions.Item span={2} label={<div className="desc-label">{t("reimburse.submitTime")}</div>}>
                  {reimbursRequest.submitAt ? dayjs(reimbursRequest.submitAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={<div className="desc-label">{t("reimburse.completeTime")}</div>}>
                  {reimbursRequest.completeAt ? dayjs(reimbursRequest.completeAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
                </Descriptions.Item>

              </Descriptions>

              {reimbursRequest.type === ApplicationTypeEnum.REIMBURSEMENT && reimbursRequest.reimbursement?.items && (
                <TabDocTable
                  tabProps={{
                    tabBarExtraContent: canEdit ? <Flex justify="flex-end" align="center">
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={handleAddReimburseItem}
                        disabled={!canEdit}
                      >
                        {t("common.add")}
                      </Button>
                    </Flex>
                      : null
                  }}
                  tableName={t("reimburse.itemList")}
                  openTab={openTab}
                  tableProps={{
                    columns: reimburseColumns as AnyObject[],
                    dataSource: reimbursRequest.reimbursement.items,
                    rowKey: "id",
                    size: "small",
                    scroll: { x: "max-content" }
                  }}
                  docProps={{
                    isRead: !canEdit,
                    docId: openTab?.docId ?? '',
                    forceReset: true,
                    // handleNewDocId(id) {
                    //   openTab && setOpenTab({ ...openTab, docId: id });
                    // },
                  }}
                  onClose={() => setOpenTab(undefined)}
                />
              )}

              {reimbursRequest.type === ApplicationTypeEnum.DAILY_SUPPLIES && reimbursRequest.dailySupplies?.items && (
                <TabDocTable
                  tabProps={{
                    tabBarExtraContent: canEdit ? <Flex justify="flex-end" align="center">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "CODE",
                              label: t("reimburse.selectProducts"),
                              onClick: () => handleAddDailyItem("CODE"),
                            },
                            {
                              key: "NO_CODE",
                              label: t("reimburse.addDailyItem"),
                              onClick: () => handleAddDailyItem("NO_CODE"),
                            },
                          ],
                        }}
                        disabled={!canEdit}
                      >
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          disabled={!canEdit}
                        >
                          {t("common.add")}
                        </Button>
                      </Dropdown>
                    </Flex>
                      : null
                  }}
                  tableName={t("reimburse.itemList")}
                  openTab={openTab}
                  tableProps={{
                    columns: dailyColumns as AnyObject[],
                    dataSource: reimbursRequest.dailySupplies.items,
                    rowKey: "id",
                    size: "small",
                    scroll: { x: "max-content" },
                    pagination: { pageSize: canEdit ? 5 : 10 }
                  }}
                  docProps={{
                    isRead: !canEdit,
                    docId: openTab?.docId ?? '',
                    forceReset: true,
                    // handleNewDocId(id) {
                    //   openTab && setOpenTab({ ...openTab, docId: id });
                    // },
                  }}
                  onClose={() => setOpenTab(undefined)}
                />
              )}
            </>
          )}
        </div>
      </div>
    </RequestContentWrap>

    {/* 报销项目新增/编辑弹窗 */}
    <AddOrEditReimburseItem
      open={openReimburseItemModal}
      applicationId={reimbursRequest?.reimbursement?.id ?? ''}
      editingItem={editingReimburseItem}
      treeData={preparedTree}
      onSuccess={handleReimburseItemSuccess}
      onClose={() => {
        setOpenReimburseItemModal(false);
        setEditingReimburseItem(null);
      }}
    />

    {/* 日用品项目新增/编辑弹窗 */}
    <AddOrEditDailyItem
      open={openDailyItemModal}
      applicationId={reimbursRequest?.dailySupplies?.id ?? ''}
      editingItem={editingDailyItem}
      productMode={productMode}
      onSuccess={handleDailyItemSuccess}
      onClose={() => {
        setOpenDailyItemModal(false);
        setEditingDailyItem(null);
      }}
    />
  </DetailModal>
};

export default DetailAndApprove;
